/**
 * AIActivityCard — Dashboard sidebar card showing recent AI activity for a case.
 *
 * Compact summary: agent badges, last few actions, quick stats.
 * Designed for the AccountantDashboard sidebar alongside other insight cards.
 */

import React, { useState, useEffect } from 'react';
import { Brain, RefreshCw, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { getCaseAIActivity, getCaseAISummary, type AIActivityEntry, type AIActivitySummary } from '../../services/aiActivityApi';

interface AIActivityCardProps {
  reportId?: string;
  className?: string;
}

const AGENT_DOT: Record<string, string> = {
  'Report Narrator': 'bg-purple-500',
  'Intelligence Agent': 'bg-blue-500',
  'Forensic Analyzer': 'bg-amber-500',
  'Settlement Calculator': 'bg-green-500',
  'Future Closure Engine': 'bg-red-500',
  'Edge Case Narrator': 'bg-teal-500',
};

function timeAgo(iso: string): string {
  try {
    const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffD = Math.floor(diffHr / 24);
    return `${diffD}d ago`;
  } catch {
    return '';
  }
}

export default function AIActivityCard({ reportId, className = '' }: AIActivityCardProps) {
  const [entries, setEntries] = useState<AIActivityEntry[]>([]);
  const [summary, setSummary] = useState<AIActivitySummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reportId) {
      setEntries([]);
      setSummary(null);
      return;
    }
    setLoading(true);
    Promise.all([
      getCaseAIActivity(reportId, { limit: 5 }),
      getCaseAISummary(reportId),
    ])
      .then(([act, sum]) => {
        setEntries(act);
        setSummary(sum);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [reportId]);

  // Empty state — no case selected
  if (!reportId) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-5 ${className}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Brain className="w-5 h-5 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">AI Activity</h3>
        </div>
        <p className="text-xs text-gray-400 text-center py-4">Select a case to view AI activity</p>
      </div>
    );
  }

  // Loading
  if (loading && entries.length === 0) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-5 ${className}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Brain className="w-5 h-5 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">AI Activity</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <RefreshCw className="w-4 h-4 text-violet-400 animate-spin mr-2" />
          <span className="text-xs text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  const agentNames = summary ? Object.keys(summary.agents) : [];

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-5 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Brain className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">AI Activity</h3>
            {summary && summary.total_interactions > 0 && (
              <p className="text-[10px] text-gray-400">{summary.total_interactions} total actions</p>
            )}
          </div>
        </div>
      </div>

      {/* Agent pills */}
      {agentNames.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {agentNames.map((name) => (
            <span key={name} className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${AGENT_DOT[name] || 'bg-gray-400'}`} />
              {name.split(' ').pop()} ({summary?.agents[name]?.count})
            </span>
          ))}
        </div>
      )}

      {/* Recent entries */}
      {entries.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">No AI activity yet</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <div
              key={e.activity_id}
              className={`flex items-start gap-2 text-xs rounded-md px-2 py-1.5 ${
                e.status === 'error' ? 'bg-red-50' : 'bg-gray-50'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${AGENT_DOT[e.agent_name] || 'bg-gray-400'}`} />
              <div className="min-w-0 flex-1">
                <p className="text-gray-700 font-medium truncate">{e.action}</p>
                <p className="text-gray-400 truncate">{e.output_summary || e.input_summary}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 text-gray-400">
                {e.status === 'error' && <AlertCircle className="w-3 h-3 text-red-400" />}
                <Clock className="w-3 h-3" />
                <span>{timeAgo(e.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
